import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui';

export interface ArtistDeleteModalProps {
  isOpen: boolean;
  artistName: string;
  productCount: number;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ArtistDeleteModal = ({
  isOpen,
  artistName,
  productCount,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: ArtistDeleteModalProps) => {
  const { t } = useTranslation('artists');
  const blocked = productCount > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="artist-delete-modal" className="max-w-md">
        <DialogTitle>{t('artists.delete.title')}</DialogTitle>
        <DialogDescription>
          {t('artists.delete.subject', { name: artistName })}
        </DialogDescription>

        {blocked ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-3 text-sm"
            style={{
              background: 'var(--status-warn-bg)',
              color: 'var(--status-warn-fg)',
            }}
          >
            {t('artists.delete.blocked', { count: productCount })}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm" style={{ color: 'var(--content-default)' }}>
              {t('artists.delete.confirm')}
            </p>
            <p
              role="alert"
              aria-live="polite"
              className="rounded-md p-3 text-sm font-medium"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {t('artists.delete.warning')}
            </p>
          </div>
        )}

        {errorMessage ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-2 text-sm"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('artists.actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={onConfirm}
            disabled={isSubmitting || blocked}
            data-testid="artist-delete-confirm"
          >
            {isSubmitting
              ? t('artists.delete.submitting')
              : t('artists.actions.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
